import { memo } from 'react';
import { Inbox } from 'lucide-react';
import { Button } from './Button.js';
import type { EmptyStateProps } from './types/screenState.types.js';

const EMPTY_ICON_CLASS =
  'h-[length:var(--icon-size-lg)] w-[length:var(--icon-size-lg)] shrink-0 stroke-[var(--icon-stroke,1.75)]';

/**
 * Reusable empty state component
 * Displays engaging empty states with helpful messages and CTAs
 */
export const EmptyState = memo<EmptyStateProps>(
  ({ leading, icon, title, message, hint, compact = false, action }) => {
    const showStringIcon = leading == null && icon != null && icon.length > 0;
    const paddingClass = compact
      ? 'px-[var(--space-4)] py-[var(--space-6)]'
      : 'px-[var(--space-8)] py-16';

    return (
      <div
        className={`rounded-xl border-2 border-dashed border-[var(--color-an-border)] bg-[var(--color-an-surface-muted)] text-center ${paddingClass}`}
        role="status"
      >
        {leading != null ? (
          <div
            className="mb-[var(--space-5)] flex justify-center text-[var(--color-an-text-muted)] opacity-60"
            aria-hidden="true"
          >
            {leading}
          </div>
        ) : null}
        {showStringIcon ? (
          <div
            className="mb-[var(--space-5)] flex justify-center text-[var(--color-an-text-muted)] opacity-60"
            aria-hidden="true"
          >
            <Inbox className={EMPTY_ICON_CLASS} aria-hidden="true" />
          </div>
        ) : null}
        <h3 className="m-0 mb-[var(--space-3)] text-[length:var(--font-size-h4)] font-[number:var(--font-weight-bold)] text-[var(--color-an-text)]">
          {title}
        </h3>
        <p className="m-0 mb-[var(--space-2)] text-[length:var(--font-size-md,var(--font-size-base))] leading-relaxed text-[var(--color-an-text-muted)]">
          {message}
        </p>
        {hint ? (
          <p className="m-0 mb-[var(--space-5)] text-[length:var(--font-size-caption)] italic leading-snug text-[var(--color-an-text-disabled)]">
            {hint}
          </p>
        ) : null}
        {action ? (
          <Button
            type="button"
            intent="primary"
            size="md"
            className="mt-[var(--space-4)]"
            onClick={action.onClick}
          >
            {action.label}
          </Button>
        ) : null}
      </div>
    );
  },
);

EmptyState.displayName = 'EmptyState';
